/**
 * Derives stock UI fields from the func-bsm-api stock enforcement change.
 *
 * StockGet, SingleStockGet, ListItemsGet, and CartGet all now return
 * `isInStock`, `quantityOnHand`, `quantityAvailable`, and `lowStockThreshold`
 * per item. Untracked items (no inventory record) come back with
 * `isInStock: true` and null quantities - null means "unlimited," not "0," so
 * it must never be treated as out of stock.
 */
export function computeStockStatus(item) {
  const isInStock = item?.isInStock;
  const quantityAvailable = item?.quantityAvailable;
  const lowStockThreshold = item?.lowStockThreshold;

  let isOutOfStock = false;
  if (typeof isInStock === "boolean") {
    isOutOfStock = !isInStock;
  } else if (typeof quantityAvailable === "number") {
    isOutOfStock = quantityAvailable <= 0;
  }

  // Only meaningful once we know the item isn't already out of stock and the
  // backend actually sent both numbers - a missing threshold means the item
  // isn't being low-stock tracked at all.
  const isLowStock =
    !isOutOfStock &&
    typeof quantityAvailable === "number" &&
    typeof lowStockThreshold === "number" &&
    quantityAvailable <= lowStockThreshold;

  return {
    isOutOfStock,
    isLowStock,
    quantityOnHand: item?.quantityOnHand ?? null,
    quantityAvailable: quantityAvailable ?? null,
    lowStockThreshold: lowStockThreshold ?? null,
  };
}

export function lowStockMessage(quantityAvailable) {
  if (typeof quantityAvailable !== "number") {
    return "Low Stock";
  }
  return quantityAvailable === 1
    ? "Only 1 left"
    : `Only ${quantityAvailable} left`;
}

// New 409 codes returned by CartItemsPost, CartItemPut, and CartPurchasePost
// when an operation would exceed available stock.
export const OUT_OF_STOCK_ERROR_CODES = new Set([
  "OUT_OF_STOCK",
  "INSUFFICIENT_STOCK",
]);

export function isOutOfStockError(error) {
  return OUT_OF_STOCK_ERROR_CODES.has(error?.code);
}
